import { BodyPrimary } from '@liveauctioneers/hammer-ui-core/text';
import { BuyNowStatus } from '@/types/item/ItemSummary';
import { calendarEvent } from '@/utils/calendarEvent';
import { Event } from '@/types/Event';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { fromUnixTime } from 'date-fns/fromUnixTime';
import {
    getCatalogDescription,
    getCatalogSellerName,
    getCatalogStartTime,
    getCatalogTitle,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import {
    getCatalogRegistration,
    getRegistrationNotify,
} from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import { getItemSummary } from '@/redux/modules/item/summary/itemSummary.selectors';
import { getSellerInInvisibleApprovalBeta } from '@/redux/modules/seller';
import { getTotalBidLimitBalance } from '@/redux/modules/bidLimit';
import { Info } from '@liveauctioneers/hammer-ui-core/icons';
import { InlineButton } from '@liveauctioneers/hammer-ui-core/inlineButton';
import { isToday } from 'date-fns/isToday';
import { longFormatDate, timeOnlyDate } from '@liveauctioneers/caterwaul-components/lib/utils/dates';
import { toggleCatalogNotifications } from '@/redux/modules/catalog/registration/catalogRegistration.actions';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import AddToCalendarLink from '@liveauctioneers/caterwaul-components/lib/AddToCalendar/AddToCalendarLink';
import BidLimitMessage from './BidLimitMessage';
import FormattedCurrency from '@/components/Formatted/FormattedCurrency';
import isEqual from 'react-fast-compare';
import React, { SyntheticEvent, useCallback } from 'react';
import RegisterLink from '@/components/RegisterLink/RegisterLink';
import styled from 'styled-components';
import Tooltip from '@liveauctioneers/caterwaul-components/lib/Tooltip/Tooltip';
import useIsServerSideRendered from '@/hooks/useIsServerSideRendered';

type Props = {
    catalogId: number;
    className?: string;
    itemId: number | null | undefined;
    showSendSellerMessageModalDeclined: (event: SyntheticEvent) => void;
};

const RegistrationMessage = ({ catalogId, className, itemId, showSendSellerMessageModalDeclined }: Props) => {
    const {
        bidderHasBid = false,
        buyNowStatus,
        catalogStatus = '',
        currency = 'usd',
        isCatalogOnly,
        isPassed,
        isSold = false,
        isTimedPlusAuction,
        saleStartTs = 0,
        sellerId,
    } = useAppSelector((state) => getItemSummary(state, itemId));
    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();

    const { approved, bidLimit, blockedByAuctioneer, declined, limited, pending, registered, suspended } =
        useAppSelector((state) => getCatalogRegistration(state, catalogId));
    const totalCatalogBidLimitBalance = useAppSelector((state) => getTotalBidLimitBalance(state, catalogId));

    const title = useAppSelector((state) => getCatalogTitle(state, catalogId));
    const sellerName = useAppSelector((state) => getCatalogSellerName(state, catalogId));
    const description = useAppSelector((state) => getCatalogDescription(state, catalogId));
    const catalogSaleStartTs = useAppSelector((state) => getCatalogStartTime(state, catalogId));
    const sellerInInvisibleApprovalBeta = useAppSelector((state) => getSellerInInvisibleApprovalBeta(state, sellerId));
    const notify = useAppSelector((state) => getRegistrationNotify(state, catalogId));
    const isSSR = useIsServerSideRendered();

    const handleToggleNotifications = useCallback(() => {
        return dispatch(toggleCatalogNotifications({ catalogId, notify: !notify }));
    }, [catalogId, dispatch, notify]);

    // both of these are used in the case that the user is auto/invisible approved
    let showAutoApproved = false;
    let event: Event;

    const buyNowAvailable = buyNowStatus === BuyNowStatus.Available && catalogStatus === 'done';
    if (buyNowAvailable) {
        return null;
    }

    const isCatalogLive = catalogStatus === 'live';

    let showRegisterLink = false;
    let registrationMessage = `registration.${isTimedPlusAuction ? 'timedPlus.' : ''}`;
    let extraValues: any = {
        contactAuctioneer: (
            <InlineButton
                onClick={showSendSellerMessageModalDeclined}
                size="sm"
            >
                {formatMessage({ id: 'registration.contact_auctioneer' })}
            </InlineButton>
        ),
        date: <span suppressHydrationWarning>{longFormatDate(saleStartTs)}</span>,
        today: isToday(fromUnixTime(saleStartTs)) ? (
            <FormattedMessage
                id="registration.liveMessages.today"
                values={{ time: timeOnlyDate(saleStartTs) }}
            />
        ) : (
            <FormattedMessage
                id="registration.liveMessages.yesterday"
                values={{ time: timeOnlyDate(saleStartTs) }}
            />
        ),
    };

    if (isCatalogLive) {
        if (pending) {
            registrationMessage += 'liveMessages.pending';
        } else if (limited) {
            registrationMessage = 'registration.liveMessages.limited';
            extraValues = {
                ...extraValues,
                balance: (
                    <FormattedCurrency
                        convert
                        key="registration-message-balance"
                        noFraction
                        sourceCurrency={currency}
                        value={totalCatalogBidLimitBalance}
                    />
                ),
                limit: (
                    <FormattedCurrency
                        convert
                        key="registration-message-limit"
                        noFraction
                        sourceCurrency={currency}
                        value={bidLimit}
                    />
                ),
            };
        } else if (approved) {
            registrationMessage += 'liveMessages.approved';
        } else if (declined || blockedByAuctioneer) {
            registrationMessage = 'registration.liveMessages.declined';
        } else if (suspended) {
            registrationMessage = 'registration.liveMessages.suspended';
        } else if (!registered) {
            registrationMessage = 'registration.liveMessages.notRegistered';
            extraValues = {
                ...extraValues,
                register: (
                    <RegisterLink
                        catalogId={catalogId}
                        itemId={itemId}
                        registerToBidMessageId="registration.register"
                    />
                ),
            };
        }
    } else {
        if (suspended) {
            registrationMessage = 'registration.suspended_with_date';
        } else if (declined || blockedByAuctioneer) {
            if (isSold) {
                registrationMessage = 'registration.sold_auction.declined';
            } else {
                registrationMessage = 'registration.declined_with_date';
            }
        } else if (limited && !isCatalogOnly) {
            return <BidLimitMessage itemId={itemId} />;
        } else if (approved && !isCatalogOnly) {
            if (isSold) {
                registrationMessage = 'registration.sold_auction.approved';
            } else if (sellerInInvisibleApprovalBeta) {
                showAutoApproved = true;
                event = calendarEvent({
                    catalogId,
                    description,
                    saleStartTs: catalogSaleStartTs,
                    sellerName,
                    title,
                });
                registrationMessage = 'registration.autoApproval.auctionStatusApproved';
            } else {
                registrationMessage += 'auction_status_approved';
            }
        } else if ((pending || (pending === undefined && bidderHasBid)) && !isCatalogOnly) {
            if (isSold) {
                registrationMessage = 'registration.sold_auction.pending';
            } else {
                registrationMessage += 'auction_status_pending';
            }
        } else if (!isCatalogOnly) {
            if (!isSold) {
                showRegisterLink = !isPassed;
                registrationMessage = 'registration.auction_status_not_registered';
            }
        }
    }

    const message =
        registrationMessage && registrationMessage !== `registration.${isTimedPlusAuction ? 'timedPlus.' : ''}` ? (
            <FormattedMessage
                id={registrationMessage}
                tagName="span"
                values={{ ...extraValues }}
            />
        ) : null;

    return showAutoApproved ? (
        <StyledAutoApprovedMessage>
            <StyledMessage className={className}>
                <BodyPrimary>{message}</BodyPrimary>
                <Tooltip
                    trigger={
                        <Info
                            className="ml-xs"
                            color="primary"
                            size="sm"
                            style="fill"
                        />
                    }
                >
                    <BodyPrimary>
                        <FormattedMessage id="registration.autoApproval.withoutRegistrationMessage" />
                    </BodyPrimary>
                </Tooltip>
            </StyledMessage>
            <div>
                {!notify ? (
                    <InlineButton
                        onClick={handleToggleNotifications}
                        size="sm"
                    >
                        {formatMessage({
                            id: 'registration.autoApproval.followThisAuction',
                        })}
                    </InlineButton>
                ) : (
                    <InlineButton
                        onClick={handleToggleNotifications}
                        size="sm"
                        style="neutral"
                    >
                        {formatMessage({
                            id: 'registration.autoApproval.unfollow',
                        })}
                    </InlineButton>
                )}
                <StyledSeparator>|</StyledSeparator>
                {!isSSR && (
                    <StyledAddToCalendarLink
                        event={event}
                        keyName="add-to-calendar"
                        label={formatMessage({
                            id: 'registration.autoApproval.addToCalendar',
                        })}
                        onClick={handleToggleNotifications}
                        target="_blank"
                    />
                )}
            </div>
        </StyledAutoApprovedMessage>
    ) : (
        <StyledRegistrationMessage
            className={className}
            suppressHydrationWarning
        >
            {message}
            {showRegisterLink && (
                <StyledRegisterLink
                    catalogId={catalogId}
                    itemId={itemId}
                    registerToBidMessageId="registration.register"
                />
            )}
        </StyledRegistrationMessage>
    );
};

export default React.memo<Props>(RegistrationMessage, isEqual);

const StyledRegistrationMessage = styled.div`
    margin: 32px 0 0;
    display: flex;
    flex-wrap: wrap;

    [data-testid='popover'] {
        display: inline-block;
        height: 20px;
        vertical-align: middle;
        & > div {
            height: 20px;
        }
    }

    @media print {
        display: none;
    }
`;

const StyledRegisterLink = styled(RegisterLink)`
    margin-left: 6px;
`;

const StyledAutoApprovedMessage = styled.div`
    margin-top: ${({ theme }) => theme.spacing.lg};
`;

const StyledMessage = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    margin-bottom: ${({ theme }) => theme.spacing.sm};

    @media print {
        display: none;
    }
`;

const StyledSeparator = styled.div`
    display: inline-block;
    margin: 0 ${({ theme }) => theme.spacing.sm};
    color: ${({ theme }) => theme.color.border.divider.default};
`;

const StyledAddToCalendarLink = styled(AddToCalendarLink)`
    text-decoration: none;
    &:hover {
        text-decoration: underline;
    }
`;
